import clsx from 'clsx';
import React, { useRef, useState } from 'react';

import CustomToaster from '@components/CustomToaster';
import useAwarnessBarVisibility from '@hooks/useAwarnessBarVisibility';
import useElementsHeight from '@hooks/useElementsHeight';
import useScrollPosition from '@hooks/useScrollPosition';
import useWindowSize from '@hooks/useWindowSize';

import { NavProps } from '../interfaces';
import NavContent from './NavContent';

const FullNav = ({
  analytics,
  currentUser,
  flashMessages,
  overrideOfferCode,
  proNavSections,
}: NavProps): JSX.Element => {
  const [isScrollStyle, setIsScrollStyle] = useState(currentUser.shouldShowScrolledNav);
  const navRef = useRef(null);
  const { width: windowWidth } = useWindowSize();
  useElementsHeight({
    cssVariable: 'nav-awareness-bar-height',
    dependency: [windowWidth],
    isScrollStyle,
    selectors: ['.top-sticky-banner'],
  });
  const isAwarnessBarVisible = useAwarnessBarVisibility(isScrollStyle);

  useScrollPosition({
    effect: ({ currPos }) => {
      if (!currentUser.shouldShowScrolledNav) {
        // show scrolled nav effect if user scrolls past top nav
        const showScrollEffect =
          navRef.current?.offsetHeight &&
          navRef.current?.offsetHeight + navRef.current?.offsetTop < Math.abs(currPos.y);
        setIsScrollStyle(showScrollEffect);
      }
    },
    deps: [navRef.current?.offsetHeight, currentUser.shouldShowScrolledNav],
  });

  return (
    <div
      ref={navRef}
      className={clsx('h-56 print:hidden nav:pt-40', {
        'fixed nav:h-64': currentUser.shouldShowScrolledNav,
        'nav:h-[164px]': !currentUser.shouldShowScrolledNav,
        'border-0 border-b border-solid border-grayscale-charcoal nav:border-b-0': !isScrollStyle,
      })}
    >
      <div
        id="global-nav-wrapper"
        className={clsx('border-6 top-0 z-10 px-10 py-12 md:py-0 nav:border-none nav:px-28', {
          'fixed left-0 right-0 top-0 h-auto bg-grayscale-charcoal': isScrollStyle,
          'relative h-full': !isScrollStyle,
          'top-[calc(var(--nav-awareness-bar-height,0px))]': isAwarnessBarVisible,
        })}
      >
        <NavContent
          analytics={analytics}
          className="mx-auto max-w-container-lg"
          currentUser={currentUser}
          isScrollStyle={isScrollStyle}
          overrideOfferCode={overrideOfferCode}
          proNavSections={proNavSections}
        />
        <CustomToaster flashMessages={flashMessages} />
      </div>
    </div>
  );
};

export default FullNav;
